import {
    ProcessCellForExportParams,
    ProcessHeaderForExportParams,
    GridApi,
    ColumnApi,
    ExcelCell,
    ExcelDataType,
    ColDef
} from 'ag-grid-community';
import {currencyFormatter, getDisplayText} from './helpers';
import {TOTAL_ROW_ID, TOTAL_WEEKLY_ROW_ID} from './ag-grid-builder';

const EXCEL_MAX_SHEET_NAME_LENGTH = 31;
const MIN_COL_WIDTH = 100;

export type CustomCellBlock = { [key: string]: { data?: any, direction?: 'down' | 'across', bold?: boolean } };

export interface PlacedCellBlock {
    cells: CustomCellBlock;
    // Style prefix, so a block can carry its own look (e.g. the bordered tax table)
    color?: string;
    // Column field the block's value cells line up under, e.g. 'netRevenue'
    alignValuesToField?: string;
}

interface ComposedBlock {
    cells: CustomCellBlock;
    color: string;
    startColumn: number;
}

function isPlacedCellBlock(block: CustomCellBlock | PlacedCellBlock): block is PlacedCellBlock {
    const candidate = block as PlacedCellBlock;
    return typeof candidate.cells === 'object'
        && candidate.cells !== null
        && (typeof candidate.color === 'string' || typeof candidate.alignValuesToField === 'string');
}

// This function is not covered by tests
export function exportToExcel(
    fileName: string,
    gridApi: GridApi,
    columnApi: ColumnApi,
    excludedColumns: string[] = [],
    hasTotalRow: boolean = false,
    footerData?: CustomCellBlock | (CustomCellBlock | PlacedCellBlock)[],
    headerData?: CustomCellBlock[],
    wrappingHeader?: string,
    agGridToExcelMagicConversion = 1.68,
    showHiddenColumns: string[] = [],
) {
    const exportParams: any = {};
    const allIncludedColumns = columnApi.getAllColumns()
        .filter(col => {
            const parent = col.getParent()?.isExpanded();
            const colGroupShow = col.getColDef().columnGroupShow;
            return parent === null
                || !colGroupShow
                || colGroupShow === 'always'
                || (parent && colGroupShow === 'open')
                || (!parent && colGroupShow === 'closed');
        })
        .filter(col => (!col.getColDef().hide || showHiddenColumns.includes(col.getColDef().field) || col.isRowGroupActive())
                && !excludedColumns.includes(col.getColDef().field))
        .map(col => col.getColId());
    exportParams.columnKeys = allIncludedColumns;

    const firstVisibleColumn = columnApi.getAllColumns()
        .find(col => !col.getColDef().hide && !excludedColumns.includes(col.getColDef().field));

    exportParams.skipGroups = true;
    exportParams.processCellCallback = (params: ProcessCellForExportParams) => {
        if (!params.node.data) {
            return;
        }
        const dataValue = params.node.data[params.column.getColDef().field];
        if (dataValue && dataValue.current) {
            return currencyFormatter(dataValue.current, '1.2-2');
        } else if (hasTotalRow && params.node.data.id === TOTAL_ROW_ID && params.column === firstVisibleColumn) {
            return 'Total:';
        } else if (hasTotalRow && params.node.data.id === TOTAL_WEEKLY_ROW_ID && params.column === firstVisibleColumn) {
            return 'Weekly Spend Total:';
        }
        let isPercent = false;
        if (params.node.data.format?.type === 'percent' || params.column.getColDef().headerComponentParams.formatType === 'percent') {
            isPercent = true;
        }
        return params.value && params.value.hasOwnProperty('name')
            ? params.value.name
            : isPercent && Number(params.value)
                ? params.value / 100
                : params.value;
    };
    exportParams.processHeaderCallback = (params: ProcessHeaderForExportParams) => {
        return params.column.getColDef()?.headerComponentParams.name;
    };

    if (footerData) {
        exportParams.customFooter = composeBlocks(
            toComposedBlocks(
                Array.isArray(footerData) ? footerData : [footerData],
                'white-background',
                exportParams.columnKeys
            ),
            'white-background',
            exportParams.columnKeys.length
        );
        exportParams.customFooter.unshift([{data: {value: '', type: 'String' as ExcelDataType}}, {
            data: {
                value: '',
                type: 'String' as ExcelDataType
            }
        }]);
    }

    exportParams.customHeader = [];
    if (headerData) {
        exportParams.customHeader = composeBlocks(
            toComposedBlocks(headerData, 'gray-background', exportParams.columnKeys),
            'gray-background',
            exportParams.columnKeys.length
        );
    }
    if (wrappingHeader) {
        exportParams.customHeader.unshift([
            {
                data: {value: wrappingHeader, type: 'String'},
                styleId: 'header',
                mergeAcross: exportParams.columnKeys.length - 1
            }]);
    }

    exportParams.columnGroups = true;
    exportParams.columnWidth = (params) => {
        if (params.column?.getColDef().cellRendererFramework && params.column.getActualWidth() > 200) {
            return Math.max(params.column?.getActualWidth() / 2 || MIN_COL_WIDTH * 2);
        }
        return Math.max(params.column?.getActualWidth() / agGridToExcelMagicConversion, MIN_COL_WIDTH);
    };
    exportParams.fileName = fileName.replace(/[^A-Za-z0-9 -]/g, ' ').trim();
    exportParams.sheetName = exportParams.fileName.substr(0, EXCEL_MAX_SHEET_NAME_LENGTH);
    gridApi.exportDataAsExcel(exportParams);
}

export function composeBlocks(blocks: ComposedBlock[], fillColor: string, totalWidth: number): ExcelCell[][] {
    const rendered: ExcelCell[][] = [];
    let nextColumn = 0;
    blocks.forEach((block, index) => {
        const startColumn = Math.min(Math.max(nextColumn, block.startColumn), totalWidth);
        generateCustomCells(
            block.cells,
            totalWidth - startColumn,
            block.color,
            index === blocks.length - 1,
            fillColor
        ).filter(row => row).forEach((row, rowNum) => {
            if (!rendered[rowNum]) {
                rendered[rowNum] = [];
            }
            fillTableWithColor(rendered[rowNum], startColumn, fillColor);
            rendered[rowNum].push(...row);
        });
        nextColumn = Math.max(startColumn, ...rendered.map(row => row.length));
    });
    rendered.forEach(row => {
        fillTableWithColor(row, totalWidth, fillColor);
    });
    return rendered;
}

function toComposedBlocks(
    blocks: (CustomCellBlock | PlacedCellBlock)[],
    defaultColor: string,
    columnKeys: string[],
): ComposedBlock[] {
    return blocks.map(block => {
        if (!isPlacedCellBlock(block)) {
            return {cells: block as CustomCellBlock, color: defaultColor, startColumn: 0};
        }
        // Values sit in the aligned column, so the label column starts one to its left
        const valueColumn = block.alignValuesToField
            ? columnKeys.indexOf(block.alignValuesToField)
            : -1;
        return {
            cells: block.cells,
            color: block.color ?? defaultColor,
            startColumn: valueColumn > 0 ? valueColumn - 1 : 0,
        };
    });
}

function generateCustomCells(
    data: CustomCellBlock,
    remainingLineLength: number,
    color: string,
    isLast: boolean = false,
    padColor: string = color,
): ExcelCell[][] {
    const customCells: ExcelCell[][] = [];
    Object.keys(data)
        .forEach(key => {
            if (data[key].data instanceof Array) {
                if (data[key].data.length > 0) {
                    if (data[key].direction === 'across') {
                        const row: ExcelCell[] = [];
                        row.push({data: {value: key, type: 'String' as ExcelDataType}, styleId: color + '-key-across'});
                        row.push(...data[key].data.map(line => (
                            {
                                data: {value: getDisplayText(line), type: line ? 'String' : 'Boolean'},
                                styleId: data[key].bold ? color + '-key' : color
                            }
                        )));
                        if (isLast) {
                            fillTableWithColor(row, remainingLineLength, padColor);
                        } else {
                            fillTableWithColor(row, row.length + 1, padColor);
                        }
                        customCells.push(row);


                    } else {
                        const row: ExcelCell[] = [];
                        row.push({data: {value: key, type: 'String' as ExcelDataType}, styleId: color + '-key'});
                        row.push({
                            data: {value: getDisplayText(data[key].data[0]), type: 'String' as ExcelDataType},
                            styleId: data[key].bold ? color + '-key' : color
                        });
                        if (isLast) {
                            fillTableWithColor(row, remainingLineLength, padColor);
                        } else {
                            fillTableWithColor(row, row.length + 1, padColor);
                        }

                        customCells.push(row);

                        (data[key].data as Array<string>).slice(1).forEach(line => {
                            const nextRow: ExcelCell[] = [];
                            nextRow.push({data: {value: '', type: 'String' as ExcelDataType}, styleId: color + '-key'});
                            nextRow.push({
                                data: {value: getDisplayText(line), type: 'String' as ExcelDataType},
                                styleId: data[key].bold ? color + '-key' : color
                            });
                            if (isLast) {
                                fillTableWithColor(nextRow, remainingLineLength, padColor);
                            } else {
                                fillTableWithColor(nextRow, row.length + 1, padColor);
                            }

                            customCells.push(nextRow);
                        });
                    }

                }
            } else if (getDisplayText(data[key].data) || getDisplayText(data[key].data) === '') {
                const displayText = getDisplayText(data[key].data);
                const row: ExcelCell[] = [];
                const styleId = displayText ?
                    data[key].direction === 'across'
                        ? color + '-key-across'
                        : color + '-key'
                    : color;
                row.push({data: {value: key, type: 'String' as ExcelDataType}, styleId});
                if (displayText.trim()) {
                    row.push({
                            data: {
                                value: displayText,
                                type: 'String' as ExcelDataType
                            },
                            styleId: data[key].bold ? color + '-key' : color
                        }
                    );
                }
                if (isLast) {
                    fillTableWithColor(row, remainingLineLength, padColor);
                } else {
                    fillTableWithColor(row, row.length + 1, padColor);
                }

                customCells.push(row);
            }
        });
    return customCells;
}

// A row can already exceed lineLength when a block is placed close to the last column,
// so this pads up to it and leaves anything longer alone.
function fillTableWithColor(row: ExcelCell[], lineLength: number, color: string): ExcelCell[] {
    if (row.length < lineLength) {
        row.push(
            {
                data: {
                    value: null,
                    type: 'Boolean'
                },
                styleId: color
            }
        );
        return fillTableWithColor(
            row,
            lineLength,
            color
        );
    }
    return row;
}
